/**
 * Returns the admin nav tree filtered by the tenant's enabled-modules
 * config. When the tenant has no `modules` array (i.e. legacy or
 * platform default), all items are returned unchanged.
 */
import { useMemo } from "react";
import { adminNav, type NavItem } from "@/components/layout/nav";
import { traderNav, type TraderNavItem } from "@/components/layout/traderNav";
import { useCurrentTenant } from "@/hooks/useTenant";
import { useMe } from "@/hooks/useAuth";
import { NAV_LABEL_TO_MODULE, TRADER_NAV_LABEL_TO_MODULE, resolveEnabledModules } from "@/lib/modules";

// RBAC: every admin page is keyed by its route (`<route>:read`). These groups
// stay visible to everyone (own profile, dashboard, support) so a restricted
// user can always navigate.
const RBAC_ALWAYS = new Set(["Dashboard", "Admin Profile", "Support Tickets"]);

export function useFilteredAdminNav(): NavItem[] {
  const { data } = useCurrentTenant();
  const me = useMe();
  const modules = data?.tenant?.settings?.modules;
  const fullAccess = me.data?.fullAccess ?? true; // fail-open until loaded
  const perms = me.data?.permissions ?? [];

  return useMemo(() => {
    let nav = adminNav;
    // 1. Tenant enabled-modules filter (platform tenant + platform super-admin → skip; they see everything).
    if (data?.tenant && !data.tenant.isPlatform && !me.data?.platform) {
      const enabled = resolveEnabledModules(modules);
      nav = nav.filter((item) => {
        const key = NAV_LABEL_TO_MODULE[item.label];
        if (!key) return true; // unknown mapping → keep (fail-open)
        return enabled.has(key);
      });
    }
    // 2. RBAC permission filter — restricted (non-full-access) staff only.
    //    Each leaf is gated by its route permission; a group survives if any
    //    child does.
    if (me.data && !fullAccess) {
      const canRead = (to?: string) => !!to && (perms.includes(`${to}:read`) || perms.includes(`${to}:write`));
      nav = nav
        .map((item): NavItem | null => {
          if (RBAC_ALWAYS.has(item.label)) return item;
          if (item.children) {
            const kids = item.children.filter((c) => canRead(c.to));
            return kids.length ? { ...item, children: kids } : null;
          }
          return canRead(item.to) ? item : null;
        })
        .filter((x): x is NavItem => x !== null);
    }
    // 3. Platform-only items (e.g. Module Control) — only the hidden platform
    //    super-admin sees them; broker admins never do.
    if (!me.data?.platform) {
      const PLATFORM_ONLY = new Set(["Module Control"]);
      nav = nav
        .map((item): NavItem | null => {
          if (item.children) {
            const kids = item.children.filter((c) => !PLATFORM_ONLY.has(c.label));
            return kids.length ? { ...item, children: kids } : null;
          }
          return PLATFORM_ONLY.has(item.label) ? null : item;
        })
        .filter((x): x is NavItem => x !== null);
    }
    return nav;
  }, [data?.tenant, modules, me.data, fullAccess, perms]);
}

/** Same filter applied to the trader sidebar — so anything the broker
 * has disabled at the platform-admin level disappears from the trader's
 * left rail too (e.g. EA Marketplace, Trading Contest, Bonus & Loyalty). */
export function useFilteredTraderNav(): TraderNavItem[] {
  const { data } = useCurrentTenant();
  const modules = data?.tenant?.settings?.modules;
  const traderNavHidden = data?.tenant?.settings?.traderNavHidden;

  return useMemo(() => {
    if (!data?.tenant || data.tenant.isPlatform) return traderNav;
    const enabled = resolveEnabledModules(modules);
    const hidden = new Set(traderNavHidden ?? []);

    return traderNav.filter((item) => {
      // Per-item hide list set by broker admin trumps anything else.
      if (hidden.has(item.label)) return false;
      // Otherwise fall through to the platform module check.
      const key = TRADER_NAV_LABEL_TO_MODULE[item.label];
      if (!key) return true;
      return enabled.has(key);
    });
  }, [data?.tenant, modules, traderNavHidden]);
}
